import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomColumnDef } from '@/types/table';
import { ColumnDisplayItem, PersistedColumnConfig, MAX_PINNED_COLUMNS } from '@/types/table/columnConfig';
import {
  areColumnConfigsEqual,
  buildDefaultColumnConfig,
  buildColumnMap,
  buildDisplayItem,
  buildMergedGroups,
  buildPinnedItems,
  countPinnedUnits,
  getOrderedColumnIds,
  getVisibleColumnCount,
  togglePinGroupInConfig,
  togglePinInConfig,
  toggleVisibilityInConfig,
} from '../utils/columnConfig';

interface UseColumnConfigParams<T> {
  columns: CustomColumnDef<T>[];
  persistedConfig: PersistedColumnConfig | null;
  onSave: (config: PersistedColumnConfig) => Promise<void>;
  onReset: () => Promise<void>;
  additionalHeaderLabels?: Record<string, string>;
}

export interface UseColumnConfigResult {
  appliedConfig: PersistedColumnConfig;
  draftConfig: PersistedColumnConfig;
  displayItems: ColumnDisplayItem[];
  pinnedItems: ColumnDisplayItem[];
  unpinnedItems: ColumnDisplayItem[];
  canPinMore: boolean;
  pinnedCount: number;
  hasChanges: boolean;
  isDialogOpen: boolean;
  openDialog: () => void;
  toggleVisibility: (columnId: string) => void;
  togglePin: (columnId: string) => void;
  togglePinGroup: (columnIds: string[]) => void;
  applyChanges: () => Promise<void>;
  resetToDefault: () => Promise<void>;
  cancelChanges: () => void;
}

interface UseDraftColumnConfigStateResult {
  draftConfig: PersistedColumnConfig;
  isDialogOpen: boolean;
  setDraftConfig: Dispatch<SetStateAction<PersistedColumnConfig>>;
  openDialog: () => void;
  closeDialog: () => void;
  cancelChanges: () => void;
}

interface UseColumnConfigCollectionsResult<T> {
  columnMap: Map<string, CustomColumnDef<T>>;
  mergedGroups: ReturnType<typeof buildMergedGroups>;
  displayItems: ColumnDisplayItem[];
  pinnedItems: ColumnDisplayItem[];
  unpinnedItems: ColumnDisplayItem[];
  pinnedCount: number;
  canPinMore: boolean;
}

const useDraftColumnConfigState = (appliedConfig: PersistedColumnConfig): UseDraftColumnConfigStateResult => {
  const [draftConfig, setDraftConfig] = useState<PersistedColumnConfig>(appliedConfig);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const previousAppliedConfigRef = useRef(appliedConfig);

  useEffect(() => {
    const previousAppliedConfig = previousAppliedConfigRef.current;

    if (!isDialogOpen && !areColumnConfigsEqual(previousAppliedConfig, appliedConfig)) {
      setDraftConfig(appliedConfig);
    }

    previousAppliedConfigRef.current = appliedConfig;
  }, [appliedConfig, isDialogOpen]);

  const openDialog = useCallback(() => {
    setDraftConfig(appliedConfig);
    setIsDialogOpen(true);
  }, [appliedConfig]);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const cancelChanges = useCallback(() => {
    setDraftConfig(appliedConfig);
    setIsDialogOpen(false);
  }, [appliedConfig]);

  return { draftConfig, isDialogOpen, setDraftConfig, openDialog, closeDialog, cancelChanges };
};

const useColumnConfigCollections = <T>(
  columns: CustomColumnDef<T>[],
  draftConfig: PersistedColumnConfig,
  additionalHeaderLabels?: Record<string, string>,
): UseColumnConfigCollectionsResult<T> => {
  const columnMap = useMemo(() => buildColumnMap(columns), [columns]);
  const mergedGroups = useMemo(() => buildMergedGroups(columns), [columns]);
  const pinnedIdSet = useMemo(
    () => new Set(draftConfig.pinning.map(pinEntry => pinEntry.columnId)),
    [draftConfig.pinning],
  );
  const visibleCount = useMemo(() => getVisibleColumnCount(draftConfig.visibility), [draftConfig.visibility]);
  const orderedColumnIds = useMemo(() => getOrderedColumnIds(columns), [columns]);

  const { displayItems, pinnedItems, unpinnedItems } = useMemo(() => {
    const buildDisplayItemForColumn = (columnId: string): ColumnDisplayItem =>
      buildDisplayItem(columnId, draftConfig, columnMap, pinnedIdSet, visibleCount, additionalHeaderLabels);
    const allItems = orderedColumnIds.map(buildDisplayItemForColumn);
    const pinned = buildPinnedItems(draftConfig.pinning, buildDisplayItemForColumn);
    const unpinned = allItems.filter(displayItem => !pinnedIdSet.has(displayItem.id));
    return { displayItems: allItems, pinnedItems: pinned, unpinnedItems: unpinned };
  }, [orderedColumnIds, draftConfig, columnMap, pinnedIdSet, visibleCount, additionalHeaderLabels]);

  return {
    columnMap,
    mergedGroups,
    displayItems,
    pinnedItems,
    unpinnedItems,
    pinnedCount: countPinnedUnits(draftConfig.pinning),
    canPinMore: countPinnedUnits(draftConfig.pinning) < MAX_PINNED_COLUMNS,
  };
};

export const useColumnConfig = <T>({
  columns,
  persistedConfig,
  onSave,
  onReset,
  additionalHeaderLabels,
}: UseColumnConfigParams<T>): UseColumnConfigResult => {
  const defaultConfig = useMemo(
    () => buildDefaultColumnConfig(columns, additionalHeaderLabels),
    [additionalHeaderLabels, columns],
  );
  const appliedConfig = persistedConfig ?? defaultConfig;
  const { draftConfig, isDialogOpen, setDraftConfig, openDialog, closeDialog, cancelChanges } =
    useDraftColumnConfigState(appliedConfig);
  const { columnMap, mergedGroups, displayItems, pinnedItems, unpinnedItems, pinnedCount, canPinMore } =
    useColumnConfigCollections(columns, draftConfig, additionalHeaderLabels);
  const hasChanges = !areColumnConfigsEqual(draftConfig, appliedConfig);

  const toggleVisibility = useCallback(
    (columnId: string) => {
      setDraftConfig(previousConfig => toggleVisibilityInConfig(previousConfig, columnId, mergedGroups));
    },
    [mergedGroups, setDraftConfig],
  );

  const togglePin = useCallback(
    (columnId: string) => {
      setDraftConfig(previousConfig => togglePinInConfig(previousConfig, columnId, columnMap, additionalHeaderLabels));
    },
    [additionalHeaderLabels, columnMap, setDraftConfig],
  );

  const togglePinGroup = useCallback(
    (columnIds: string[]) => {
      setDraftConfig(previousConfig =>
        togglePinGroupInConfig(previousConfig, columnIds, columnMap, additionalHeaderLabels),
      );
    },
    [additionalHeaderLabels, columnMap, setDraftConfig],
  );

  const applyChanges = useCallback(async () => {
    await onSave(draftConfig);
    closeDialog();
  }, [closeDialog, draftConfig, onSave]);

  const resetToDefault = useCallback(async () => {
    await onReset();
    setDraftConfig(defaultConfig);
    closeDialog();
  }, [closeDialog, defaultConfig, onReset, setDraftConfig]);

  return {
    appliedConfig,
    draftConfig,
    displayItems,
    pinnedItems,
    unpinnedItems,
    canPinMore,
    pinnedCount,
    hasChanges,
    isDialogOpen,
    openDialog,
    toggleVisibility,
    togglePin,
    togglePinGroup,
    applyChanges,
    resetToDefault,
    cancelChanges,
  };
};
